import React, { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { DownloadAppModal } from "@/components/ui/download-app-modal";
import LoginPromptModalDiscovery from "@/components/ui/login-prompt-modal-discovery";
import { Skeleton } from "@/components/ui/skeleton";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import type { BagItem, BagResponse } from "../types/bag";

import { useBagApi } from "../api/bag-query";
import DiscoveryFooter from "../components/index/discovery-footer";
import NavbarMinimal from "../components/index/navbar-minimal";
import useAuthStore from "../store/auth";
import { useBagStore } from "../store/bag";

interface GuestBagItemsProps {
	guestBagItems: BagItem[];
	removeGuestItem: (id: string) => void;
}

const GuestBagItems: React.FC<GuestBagItemsProps> = ({ guestBagItems, removeGuestItem }) => {
	const [selectedIdxMap, setSelectedIdxMap] = useState<Record<string, number>>({});
	const [showLoginPrompt, setShowLoginPrompt] = useState(false);
	const [showDownloadModal, setShowDownloadModal] = useState(false);
	const isAuthenticated = useAuthStore(state => state.isAuthenticated);
	const navigate = useNavigate();

	// For demo, add productName and name to BagItem (should be in type)
	type GuestBagItem = BagItem & { productName?: string; name?: string };
	const grouped: Record<string, GuestBagItem[]> = guestBagItems.reduce((acc: Record<string, GuestBagItem[]>, item: GuestBagItem) => {
		if (!acc[item.vendorId])
			acc[item.vendorId] = [];
		acc[item.vendorId].push(item);
		return acc;
	}, {});

	// API call for Buy Now
	async function handleBuyNow(productId: string, quantity: number) {
		if (!isAuthenticated) {
			setShowLoginPrompt(true);
			return;
		}
		// TODO: Replace with actual API call
		await fetch("/v1/bag/buy-now", { method: "POST", body: JSON.stringify({ productId, quantity }) });
		navigate({ to: "/bag" });
	}
	// Negotiate - redirect to download mobile app
	function handleNegotiate(_productId: string, _quantity: number) {
		setShowDownloadModal(true);
	}

	return (
		<>
			<LoginPromptModalDiscovery open={showLoginPrompt} onClose={() => setShowLoginPrompt(false)} onLogin={() => setShowLoginPrompt(false)} />
			{Object.entries(grouped).map(([vendorId, items]) => {
				const selectedIdx = selectedIdxMap[vendorId] ?? 0;
				const setSelectedIdx = (idx: number) => setSelectedIdxMap(prev => ({ ...prev, [vendorId]: idx }));
				const selected = items[selectedIdx] || items[0];
				const grandTotal = items.reduce((sum, i) => sum + (i.pricePerCard * i.quantity), 0);
				return (
					<div key={vendorId} className="bg-white rounded-lg shadow p-6 mb-6">
						{/* Images row */}
						<div className="flex gap-3 overflow-x-auto p-2 -m-2">
							{items.map((item, idx) => (
								<img
									key={item.id}
									src={item.images[0]}
									alt={item.productName || item.id}
									className={`w-[108px] h-[121px] rounded-[10px] border-2 ${selectedIdx === idx ? "border-[#3F2EC8]" : "border-transparent"} cursor-pointer bg-white object-cover transition-all duration-150 hover:scale-105 hover:shadow-md`}
									style={{ minWidth: 108, minHeight: 121 }}
									tabIndex={0}
									role="button"
									aria-label={`Select image ${idx + 1} for ${item.productName || item.id}`}
									onClick={() => setSelectedIdx(idx)}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") {
											setSelectedIdx(idx);
										}
									}}
								/>
							))}
						</div>
						{/* Vendor name, item count, quantity controls, trash */}
						<div className="flex justify-between items-center mt-4">
							<div>
								<h2 className="font-medium text-[18px] leading-[120%] text-[#2A2A2A] m-0">{selected.vendorName}</h2>
								<div className="text-xs text-gray-500 mt-1">
									{items.length}
									{" "}
									item
									{items.length > 1 ? "s" : ""}
									{" "}
									from this vendor
								</div>
							</div>
							<div className="flex items-center gap-2">
								<div className="flex items-center gap-1 bg-white border border-[#B3B3B3] rounded-[4px] px-1 h-[22px] min-w-[60px]">
									<button className="text-gray-500 w-5 h-5 flex items-center justify-center rounded hover:bg-gray-100 hover:text-[#3F2EC8] transition-colors" aria-label="Decrease quantity" onClick={() => selected.quantity > 1 && useBagStore.getState().updateQuantity(selected.id, selected.quantity - 1)}>-</button>
									<span className="mx-1 min-w-[14px] text-center">{selected.quantity}</span>
									<button className="text-gray-500 w-5 h-5 flex items-center justify-center rounded hover:bg-gray-100 hover:text-[#3F2EC8] transition-colors" aria-label="Increase quantity" onClick={() => useBagStore.getState().updateQuantity(selected.id, selected.quantity + 1)}>+</button>
								</div>
								<button className="ml-1 rounded p-1 hover:bg-red-50 transition-colors" aria-label="Remove item from bag" onClick={() => removeGuestItem(selected.id)}>
									<img src="/assets/icons/trash.svg" alt="Remove" width={16} height={16} />
								</button>
							</div>
						</div>
						{/* Product name row */}
						<div className="mt-4 text-base font-medium text-[#2A2A2A]">{selected.productName || selected.name || selected.id}</div>
						{/* Item price row */}
						<div className="flex justify-between items-center mt-2">
							<div className="text-[14px] text-[#6F6F6F] leading-[120%]">Item price</div>
							<div className="text-[14px] font-medium text-[#2A2A2A] leading-[120%]">
								₦
								{selected.pricePerCard.toLocaleString()}
							</div>
						</div>
						{/* Grand total row */}
						<div className="flex justify-between items-center mt-2">
							<div className="text-[14px] text-[#6F6F6F] leading-[120%]">Grand total</div>
							<div className="text-[20px] font-medium text-[#2A2A2A] leading-[120%]">
								₦
								{grandTotal.toLocaleString()}
							</div>
						</div>
						{/* Action buttons */}
						<div className="flex justify-between items-center mt-6 border-t pt-4">
							<button className="text-[#3F2EC8] font-medium text-[14px] leading-[120%]" onClick={() => handleBuyNow(selected.id, selected.quantity)}>Buy now</button>
							<button className="text-[#3F2EC8] font-medium text-[14px] leading-[120%]" onClick={() => handleNegotiate(selected.id, selected.quantity)}>Negotiate with vendor</button>
						</div>
					</div>
				);
			})}
			<DownloadAppModal open={showDownloadModal} onOpenChange={setShowDownloadModal} />
		</>
	);
};

const Bag: React.FC = () => {
	const navigate = useNavigate();
	const isAuthenticated = useAuthStore(state => state.isAuthenticated);
	const { getCartBags, addToBag, removeFromBag, updateBagItem } = useBagApi();
	const [bag, setBag] = useState<BagResponse | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [selectedAuthItemMap, setSelectedAuthItemMap] = useState<Record<string, number>>({});
	const [showDownloadModal, setShowDownloadModal] = useState(false);
	const guestBagItems = useBagStore(state => state.items);
	const removeGuestItem = useBagStore(state => state.removeItem);
	const resetBag = useBagStore(state => state.resetBag);

	// Sync guest bag to authenticated bag on login
	useEffect(() => {
		async function syncGuestToAuthenticated() {
			if (!isAuthenticated || guestBagItems.length === 0)
				return;

			try {
				// Migrate each guest item to authenticated bag
				for (const item of guestBagItems) {
					await addToBag({
						productId: item.productId || item.id,
						vendorId: item.vendorId,
						quantity: item.quantity,
						selectedSize: item.selectedSize,
						selectedColor: item.selectedColor,
						notes: item.notes,
						isWishlist: false
					});
				}
				// Clear guest bag after successful sync
				resetBag();
				// Refresh authenticated bag
				const data = await getCartBags();
				setBag(data);
			}
			catch (err) {
				console.error("Failed to sync guest bag:", err);
			}
		}
		syncGuestToAuthenticated();
	}, [isAuthenticated, guestBagItems, resetBag, addToBag, getCartBags]);

	// Fetch authenticated bag
	useEffect(() => {
		if (isAuthenticated) {
			getCartBags()
				.then((data) => {
					console.log("🛍️ Bag API response:", data);
					console.log("🛍️ cartBags:", data?.cartBags);
					console.log("🛍️ First cartBag:", data?.cartBags?.[0]);
					console.log("🛍️ First cartBag items:", data?.cartBags?.[0]?.items);
					console.log("🛍️ First item:", data?.cartBags?.[0]?.items?.[0]);
					setBag(data);
					setLoading(false);
				})
				.catch((err) => {
					if (err?.response?.status === 401) {
						setBag(null);
						setLoading(false);
					}
					else {
						setError("Failed to load bag");
						setLoading(false);
					}
				});
		}
		else {
			setLoading(false);
		}
	}, [isAuthenticated, getCartBags]);

	// Authenticated bag handlers
	async function handleAuthRemove(bagId: string, productId: string) {
		try {
			await removeFromBag(bagId, productId);
			const data = await getCartBags();
			setBag(data);
		}
		catch (err) {
			console.error("Failed to remove item:", err);
		}
	}

	async function handleAuthUpdateQuantity(bagId: string, productId: string, newQuantity: number) {
		if (newQuantity < 1)
			return; // Don't allow quantity below 1

		try {
			await updateBagItem(bagId, productId, { quantity: newQuantity });
			const data = await getCartBags();
			setBag(data);
		}
		catch (err) {
			console.error("Failed to update quantity:", err);
		}
	}

	// Negotiate - commented out, redirect to download mobile app
	// async function handleAuthNegotiate(bagId: string) {
	// 	const cartBag = bag?.cartBags?.find(b => b.id === bagId);
	// 	try {
	// 		const result = await negotiateWithVendor(bagId);
	// 		const roomId = result?.chatRoomId || result?.matrixRoomId || result?.roomId || result?.vendorBuyerRoomId || result?.id;
	// 		if (roomId) {
	// 			const chatData = {
	// 				bagId: result?.bagId || bagId,
	// 				vendorId: result?.vendorId || cartBag?.vendorId || "",
	// 				vendorName: result?.vendorName || cartBag?.vendorName || "Vendor",
	// 				vendorMatrixUserId: result?.vendorMatrixUserId || result?.matrixUserId || "",
	// 				totalItems: result?.totalItems || cartBag?.items?.length || 0,
	// 				totalAmount: result?.totalAmount || "0",
	// 				items: cartBag?.items || []
	// 			};
	// 			localStorage.setItem(`negotiateChat:${roomId}`, JSON.stringify(chatData));
	// 			sessionStorage.setItem("negotiateChatData", JSON.stringify(chatData));
	// 			navigate({ to: "/buyer/chat/$roomid", params: { roomid: encodeURIComponent(roomId) } });
	// 		} else {
	// 			toast.error("Could not start negotiation. Please try again.");
	// 		}
	// 	} catch (err: any) {
	// 		console.error("Failed to negotiate:", err);
	// 		toast.error(err?.response?.data?.message || "Failed to start negotiation");
	// 	}
	// }
	function handleAuthNegotiate(_bagId: string) {
		setShowDownloadModal(true);
	}

	async function handleAuthBuyNow(bagId: string) {
		navigate({
			to: "/checkout/shipping",
			search: { bagId }
		});
	}

	return (
		<div className="min-h-screen bg-[#F5F5FA] flex flex-col">
			<NavbarMinimal />
			{/* Back button: returns to previous page */}
			<div className="flex items-center mb-2">
				<button
					type="button"
					aria-label="Go back"
					className="rounded-full p-1 transition-colors duration-150 hover:bg-gray-200"
					style={{ position: "relative", lineHeight: 1, border: "none", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10, marginLeft: 12, marginTop: 12 }}
					onClick={() => window.history.back()}
				>
					<img
						src="/assets/icons/arrow-left.svg"
						alt="Back"
						style={{ width: 24, height: 24, display: "block" }}
					/>
				</button>
			</div>
			{/* Main Content */}
			{((isAuthenticated && bag && (bag.cartBags?.length ?? 0) === 0) || (!isAuthenticated && guestBagItems.length === 0))
				? (
						<div className="flex-1 flex flex-col items-center justify-center w-full min-h-[60vh] pb-16">
							<div
								className="relative bg-white shadow-2xl border border-[#ececff] text-center p-4 rounded-2xl w-full max-w-xs sm:p-6 sm:rounded-3xl sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl"
								style={{ boxShadow: "0 8px 32px rgba(63,46,200,0.12)" }}
								role="status"
							>
								<div className="flex justify-center mb-4">
									<div className="bg-[#3F2EC8] bg-opacity-10 rounded-full p-3 flex items-center justify-center">
										<img
											src="/assets/icons/shopping-bag.svg"
											alt="Bag"
											style={{
												width: 36,
												height: 36,
												display: "block",
												filter: "brightness(0.5) drop-shadow(0 2px 6px rgba(63,46,200,0.18))",
												borderRadius: "50%"
											}}
										/>
									</div>
								</div>
								<h1 className="text-2xl font-bold text-[#3F2EC8] mb-2">Your bag is empty</h1>
								<p className="mb-6 text-gray-700 text-base">Looks like you haven’t added anything to your bag yet.</p>
								<Button className="w-full" type="button" onClick={() => window.location.href = "/discovery"}>
									Go to Discovery
								</Button>
							</div>
						</div>
					)
				: (
						<div className="flex flex-col flex-1 min-h-[60vh]">
							<div className="flex flex-col md:flex-row gap-6 px-4 py-8 pb-16 max-w-6xl w-full mx-auto flex-1">
								{/* Left: Bag Items */}
								<div className="flex-1 min-w-0 max-w-full overflow-hidden">
									{/* <div className="bg-white rounded-lg shadow p-4 mb-6"> */}
									{/* <div className="flex items-center justify-between mb-4">
											<span className="font-semibold text-lg">
												Select all item (
												{isAuthenticated ? (bag?.items.length ?? 0) : guestBagItems.length}
												)
											</span>
										</div> */}
									{/* Bag Item Card */}
									{loading && (
										<div className="space-y-6">
											{[1, 2].map(i => (
												<div key={i} className="bg-white rounded-lg shadow p-6">
													{/* Image thumbnails row */}
													<div className="flex gap-3">
														<Skeleton className="w-[108px] h-[121px] rounded-[10px]" />
														<Skeleton className="w-[108px] h-[121px] rounded-[10px]" />
													</div>
													{/* Vendor name + item count */}
													<div className="mt-4 space-y-2">
														<Skeleton className="h-5 w-36" />
														<Skeleton className="h-3 w-28" />
													</div>
													{/* Product name + quantity controls */}
													<div className="flex justify-between items-center mt-4">
														<Skeleton className="h-4 w-40" />
														<Skeleton className="h-[22px] w-[60px] rounded" />
													</div>
													{/* Price per card */}
													<div className="flex justify-between items-center mt-4">
														<Skeleton className="h-4 w-24" />
														<Skeleton className="h-4 w-20" />
													</div>
													{/* Grand total */}
													<div className="flex justify-between items-center mt-2">
														<Skeleton className="h-4 w-24" />
														<Skeleton className="h-6 w-28" />
													</div>
													{/* Action buttons */}
													<div className="flex justify-between items-center mt-6 border-t pt-4">
														<Skeleton className="h-4 w-20" />
														<Skeleton className="h-4 w-36" />
													</div>
												</div>
											))}
										</div>
									)}
									{error && <div className="text-red-500">{error}</div>}
									{(!isAuthenticated && guestBagItems.length === 0) && (
										<div className="text-center text-gray-400 py-8">Your bag is empty</div>
									)}
									{(isAuthenticated && bag && bag.cartBags.length === 0) && (
										<div className="text-center text-gray-400 py-8">Your bag is empty</div>
									)}
									{isAuthenticated && bag && bag.cartBags?.map((cartBag) => {
										const selectedIdx = selectedAuthItemMap[cartBag.id] ?? 0;
										const selectedItem = cartBag.items?.[selectedIdx] || cartBag.items?.[0];

										return (
											<div key={cartBag.id} className="bg-white rounded-lg shadow p-6 mb-6 max-w-full">
												{/* Images row */}
												<div className="flex gap-3 overflow-x-auto p-2 -m-2 max-w-full">
													{cartBag.items?.map((item, idx) => (
														<img
															key={`${item.bagItemId}-${idx}`}
															src={item.productPhotos?.[0] || "/placeholder.png"}
															alt={item.productName || "Product"}
															className={`w-[108px] h-[121px] rounded-[10px] border-2 ${selectedIdx === idx ? "border-[#3F2EC8]" : "border-transparent"} cursor-pointer bg-white object-cover flex-shrink-0 transition-all duration-150 hover:scale-105 hover:shadow-md`}
															style={{ minWidth: 108, minHeight: 121 }}
															onClick={() => setSelectedAuthItemMap(prev => ({ ...prev, [cartBag.id]: idx }))}
															onKeyDown={(e) => {
																if (e.key === "Enter" || e.key === " ") {
																	setSelectedAuthItemMap(prev => ({ ...prev, [cartBag.id]: idx }));
																}
															}}
															tabIndex={0}
															role="button"
															aria-label={`Select ${item.productName || "product"}`}
														/>
													))}
												</div>
												{/* Vendor name */}
												<div className="mt-4">
													<h2 className="font-medium text-[18px] leading-[120%] text-[#2A2A2A] m-0">
														{cartBag.vendorName || "Vendor"}
													</h2>
												</div>
												{/* Number of items from vendor */}
												<div className="text-xs text-gray-500 mt-1">
													{cartBag.items?.length || 0}
													{" "}
													item
													{(cartBag.items?.length || 0) > 1 ? "s" : ""}
													{" "}
													from this vendor
												</div>
												{/* Product name, quantity controls, and delete */}
												<div className="flex justify-between items-center mt-4">
													<div className="font-medium text-[16px] text-[#2A2A2A]">
														{selectedItem?.productName || "Product"}
													</div>
													<div className="flex items-center gap-2">
														<div className="flex items-center gap-1 bg-white border border-[#B3B3B3] rounded-[4px] px-1 h-[22px] min-w-[60px]">
															<button
																className="text-gray-500 w-5 h-5 flex items-center justify-center rounded hover:bg-gray-100 hover:text-[#3F2EC8] transition-colors"
																aria-label="Decrease quantity"
																onClick={() => {
																	if (selectedItem && selectedItem.quantity > 1) {
																		handleAuthUpdateQuantity(
																			cartBag.id,
																			selectedItem.productId || selectedItem.id,
																			selectedItem.quantity - 1
																		);
																	}
																}}
															>
																-
															</button>
															<span className="mx-1 min-w-[14px] text-center">{selectedItem?.quantity || 1}</span>
															<button
																className="text-gray-500 w-5 h-5 flex items-center justify-center rounded hover:bg-gray-100 hover:text-[#3F2EC8] transition-colors"
																aria-label="Increase quantity"
																onClick={() => {
																	if (selectedItem) {
																		handleAuthUpdateQuantity(
																			cartBag.id,
																			selectedItem.productId || selectedItem.id,
																			selectedItem.quantity + 1
																		);
																	}
																}}
															>
																+
															</button>
														</div>
														<button
															className="text-red-400 rounded p-1 hover:bg-red-50 transition-colors"
															aria-label="Remove bag"
															onClick={() => {
																if (selectedItem) {
																	handleAuthRemove(cartBag.id, selectedItem.productId || selectedItem.id);
																}
															}}
														>
															<img src="/assets/icons/trash.svg" alt="Remove" width={16} height={16} />
														</button>
													</div>
												</div>
												{/* Price per card */}
												<div className="flex justify-between items-center mt-4">
													<div className="text-[14px] text-[#6F6F6F] leading-[120%]">Price per card</div>
													<div className="text-[14px] font-medium text-[#2A2A2A] leading-[120%]">
														₦
														{Number(selectedItem?.unitPrice ?? selectedItem?.productPrice ?? 0).toLocaleString()}
													</div>
												</div>
												{/* Grand total */}
												<div className="flex justify-between items-center mt-2">
													<div className="text-[14px] text-[#6F6F6F] leading-[120%]">Grand total</div>
													<div className="text-[20px] font-medium text-[#2A2A2A] leading-[120%]">
														₦
														{cartBag.items?.reduce((sum, item) => sum + Number(item.subtotal ?? 0), 0).toLocaleString()}
													</div>
												</div>
												{/* Action buttons */}
												<div className="flex justify-between items-center mt-6 border-t pt-4">
													<button
														className="text-[#3F2EC8] font-medium text-[14px] leading-[120%]"
														onClick={() => handleAuthBuyNow(cartBag.id)}
													>
														Buy now
													</button>
													<button
														className="text-[#3F2EC8] font-medium text-[14px] leading-[120%]"
														onClick={() => handleAuthNegotiate(cartBag.id)}
													>
														Negotiate with vendor
													</button>
												</div>
											</div>
										);
									})}
									{/* Group guest bag items by vendorId */}
									{!isAuthenticated && <GuestBagItems guestBagItems={guestBagItems} removeGuestItem={removeGuestItem} />}
								</div>
							</div>
							{/* Right: Payment Method
								<div className="w-full md:w-80">
									<div className="bg-white rounded-lg shadow p-4">
										<div className="font-semibold text-lg mb-2">Payment method</div>
										<div className="text-gray-700">{isAuthenticated ? (bag?.paymentMethod ?? "-") : (guestPaymentMethod || "-")}</div>
									</div>
								</div> */}
						</div>
						// </div>
					)}
			<DiscoveryFooter />
			<DownloadAppModal open={showDownloadModal} onOpenChange={setShowDownloadModal} />
		</div>
	);
};

export const Route = createFileRoute("/bag")({
	component: Bag
});

export default Bag;
